// ============================================================
// Layout.js — der äußere Rahmen der ganzen App
//
// Hier ist die Navigation drin, der Header mit Logo, User-Menü,
// Dark-Mode-Toggle, Undo/Redo-Buttons — basically alles was auf
// JEDER Seite gleich bleibt. Die eigentliche Seite (Contacts,
// Dashboard etc.) wird über <Outlet /> reingeladen.
//
// Außerdem drin: PWA-Install-Banner (App auf Homescreen),
// iOS-Install-Hint, Mobile Bottom Tab Bar (wie eine native App)
// und globales Keyboard-Shortcut für Strg+Z (Rückgängig).
// ============================================================

import React, { useEffect, useState } from 'react';
// Outlet = Platzhalter wo die aktuelle Unterseite reinkommt
// NavLink = wie Link, aber setzt automatisch eine "active"-Klasse wenn die URL passt
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme, useTour } from '@/App';
import { Button } from '@/components/ui/button';
// useUndo = Hook der die Undo/Redo-Logik verwaltet (Strg+Z in der App)
import useUndo from '@/hooks/useUndo';
// IOSInstallHint = kleiner Hinweis für iOS-User wie man die App installiert
import IOSInstallHint from '@/components/IOSInstallHint';
// TourOverlay = das Spotlight-Overlay der interaktiven Hilfe-Tour, TOURS = ihre Inhalte
import TourOverlay from '@/components/TourOverlay';
import { TOURS } from '@/tours/definitions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  LayoutDashboard, Users, Settings, Newspaper,
  LogOut, Moon, Sun, User, ChevronDown, HelpCircle,
  Undo2, Redo2, Download, X, Bug
} from 'lucide-react';
// cn = utility um CSS-Klassen dynamisch zusammenzubauen (Tailwind-freundlich)
import { cn } from '@/lib/utils';

// Logo-URL mit Cache-Buster damit Änderungen am Logo sofort sichtbar sind
const ARIWA_LOGO = "/ariwa-animal-rights-watch-logo.png?v=20260227";

const Layout = () => {
  // user = eingeloggter User, logout = Abmelde-Funktion
  const { user, logout } = useAuth();
  // theme = 'dark' oder 'light', toggleTheme = umschalten
  const { theme, toggleTheme } = useTheme();
  const { goToTour } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const currentTour = TOURS.find((tour) => tour.route === location.pathname);
  const runTour = (tour) => { goToTour(tour.id, tour.route); setHelpOpen(false); };
  // nur Admins und Editoren können Aktionen rückgängig machen — Viewer nicht
  const canUndo = user?.role === 'admin' || user?.role === 'editor' || user?.role === 'developer';
  const { lastUndo, loadingUndo, loadingRedo, hasRedo, performUndo, performRedo } = useUndo(canUndo);

  // ---- PWA Install Banner States ----
  // installPrompt = das Browser-Event das wir abfangen müssen um "Installieren" anzubieten
  const [installPrompt, setInstallPrompt] = useState(null);
  // showInstallBanner = ob der grüne Banner oben angezeigt wird
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  // isInstalled = true wenn die App bereits als PWA installiert ist
  const [isInstalled, setIsInstalled] = useState(false);

  // PWA-Install-Logik: Browser-Event abfangen und Banner anzeigen
  useEffect(() => {
    // wenn die App schon im Standalone-Modus läuft (= als PWA installiert) → nix tun
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handler = (e) => {
      // Browser-Standard-Prompt unterdrücken — wir zeigen unseren eigenen Banner
      e.preventDefault();
      setInstallPrompt(e);
      // Banner nur zeigen wenn User ihn in dieser Session nicht schon weggeklickt hat
      const dismissed = sessionStorage.getItem('pwa-banner-dismissed');
      if (!dismissed) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // wenn die App erfolgreich installiert wurde → Banner verstecken
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // handleInstall — zeigt den nativen Browser-Install-Dialog
  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    // warten ob User "Installieren" oder "Abbrechen" klickt
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setIsInstalled(true);
    setShowInstallBanner(false);
    setInstallPrompt(null);
  };

  // dismissBanner — Banner schließen und für diese Session nicht mehr zeigen
  const dismissBanner = () => {
    setShowInstallBanner(false);
    // sessionStorage (nicht localStorage) — wird beim Tab schließen gelöscht
    sessionStorage.setItem('pwa-banner-dismissed', '1');
  };

  // handleLogout — ausloggen und zur Login-Seite navigieren
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Globales Keyboard-Shortcut: Strg+Z = Rückgängig, Strg+Shift+Z / Strg+Y = Wiederholen
  // nur für Admins/Editoren aktiv, und NUR wenn kein Input-Feld fokussiert ist
  useEffect(() => {
    if (!canUndo) return undefined;
    const handleKeyDown = (event) => {
      // nur wenn Strg oder Cmd gedrückt (macOS), aber nicht Alt
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const target = event.target;
      // in Textfeldern soll der Browser sein eigenes Undo machen — nicht unsere App-Version
      const isEditable = target instanceof HTMLElement && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]')
      );
      if (isEditable) return;

      if (event.key.toLowerCase() === 'z' && !event.shiftKey) {
        if (!lastUndo) return;
        event.preventDefault(); // Browser-Standard-Undo verhindern
        performUndo();
      } else if ((event.key.toLowerCase() === 'z' && event.shiftKey) || event.key.toLowerCase() === 'y') {
        if (!hasRedo) return;
        event.preventDefault();
        performRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, lastUndo, hasRedo, performUndo, performRedo]);

  // Navigation-Items — die Hauptseiten der App. "Feedback & Issues" ist der interne
  // Bug-/Feature-Tracker — Admin/Developer haben vollen Zugriff, Editor darf ansehen/anlegen/
  // kommentieren (siehe App.js AdminRoute und backend/routes/issues.py).
  const canSeeIssuesTab = user?.role === 'admin' || user?.role === 'editor' || user?.role === 'developer';
  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/contacts', icon: Users, label: 'Kontakte' },
    { to: '/pressespiegel', icon: Newspaper, label: 'Pressespiegel' },
    ...(canSeeIssuesTab ? [{ to: '/issues', icon: Bug, label: 'Feedback' }] : []),
    { to: '/settings', icon: Settings, label: 'Einstellungen' },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">

      {/* ---- PWA Install Banner (Android/Chrome) ---- */}
      {/* wird oben über allem angezeigt wenn der Browser Install unterstützt */}
      {showInstallBanner && !isInstalled && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-primary text-primary-foreground px-4 py-3 flex items-center gap-3 shadow-lg">
          <div className="bg-white/20 rounded-lg p-1.5 shrink-0">
            <img src={ARIWA_LOGO} alt="ARIWA" className="h-6 w-auto" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">App installieren</p>
            <p className="text-xs opacity-80 leading-tight">Zum Homescreen hinzufügen</p>
          </div>
          {/* Klick → nativer Browser-Install-Dialog öffnet sich */}
          <Button size="sm" variant="secondary" className="shrink-0 h-8 text-xs font-semibold" onClick={handleInstall}>
            <Download className="h-3.5 w-3.5 mr-1" />
            Installieren
          </Button>
          {/* X-Button → Banner für diese Session schließen */}
          <button onClick={dismissBanner} className="shrink-0 p-1 rounded-full hover:bg-white/20 transition-colors" aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ---- Header — sticky oben, immer sichtbar ---- */}
      {/* backdrop-blur = der glasige Blur-Effekt wenn man runterscrollt */}
      <header className={cn(
        "sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        // wenn Install-Banner sichtbar ist: Header nach unten schieben damit er nicht überlappt
        showInstallBanner && !isInstalled && "mt-[60px]"
      )}>
        <div className="flex h-14 items-center justify-between px-4 md:px-6">

          {/* Logo — klickbar, geht zum Dashboard */}
          <NavLink to="/dashboard" className="flex items-center gap-2">
            <div className="bg-white rounded-lg p-1">
              <img src={ARIWA_LOGO} alt="ARIWA Logo" className="h-7 w-auto" />
            </div>
            {/* "Presseverteiler" Text nur auf Desktop sichtbar (hidden auf Mobile) */}
            <span className="text-lg font-bold heading-font tracking-tight hidden sm:block">
              Presseverteiler
            </span>
          </NavLink>

          {/* ---- Desktop Navigation (nur auf md+ sichtbar) ---- */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  // aktiver Link hat Primärfarbe + leichten Hintergrund
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* ---- Rechte Seite: Undo/Redo, Dark Mode, User-Menü ---- */}
          <div className="flex items-center gap-1 md:gap-2">

            {/* Undo/Redo nur für Admins/Editoren, nur auf Desktop */}
            {canUndo && (
              <div className="hidden md:flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={performUndo} disabled={!lastUndo || loadingUndo} title="Rückgängig (Strg/Cmd+Z)" data-testid="undo-btn">
                  <Undo2 className="h-4 w-4 mr-2" />
                  Rückgängig
                </Button>
                <Button variant="outline" size="sm" onClick={performRedo} disabled={!hasRedo || loadingRedo} title="Wiederholen (Strg/Cmd+Shift+Z)" data-testid="redo-btn">
                  <Redo2 className="h-4 w-4 mr-2" />
                  Wiederholen
                </Button>
              </div>
            )}

            {/* Hilfe — öffnet ein Menü mit interaktiven Touren, die die Funktionen der
                aktuellen (bzw. einer anderen) Seite direkt in der Oberfläche erklären.
                Siehe TourOverlay.js + tours/definitions.js für die eigentliche Tour-Logik. */}
            <Popover open={helpOpen} onOpenChange={setHelpOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="help-btn" aria-label="Hilfe">
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 space-y-1">
                <p className="px-1 text-sm font-semibold">Hilfe</p>
                <p className="px-1 pb-1 text-xs text-muted-foreground">
                  Interaktive Touren erklären dir die Funktionen direkt in der Oberfläche.
                </p>
                {currentTour && (
                  <button
                    type="button"
                    onClick={() => runTour(currentTour)}
                    className="flex w-full items-center gap-2 rounded-md bg-primary/10 px-2 py-2 text-left text-sm font-medium text-primary hover:bg-primary/15"
                    data-testid="help-current-tour-btn"
                  >
                    <HelpCircle className="h-4 w-4 shrink-0" /> Diese Seite erklären
                  </button>
                )}
                {TOURS.filter((tour) => tour.route && tour.id !== currentTour?.id).length > 0 && (
                  <>
                    <p className="px-1 pt-2 text-xs text-muted-foreground">Andere Themen</p>
                    {TOURS.filter((tour) => tour.route && tour.id !== currentTour?.id).map((tour) => (
                      <button
                        key={tour.id}
                        type="button"
                        onClick={() => runTour(tour)}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                      >
                        {tour.label}
                      </button>
                    ))}
                  </>
                )}
              </PopoverContent>
            </Popover>

            {/* Dark/Light Mode Toggle — Sonne = Light, Mond = Dark */}
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-9 w-9" data-testid="theme-toggle-btn">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* User-Menü — Dropdown mit Name, Rolle, Einstellungen, Logout */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1.5 px-2" data-testid="user-menu-btn">
                  {/* kleiner Avatar-Kreis mit User-Icon */}
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  {/* Name nur auf sm+ sichtbar, wird abgeschnitten wenn zu lang */}
                  <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* User-Info im Dropdown-Header */}
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize mt-0.5">Rolle: {user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                {/* Undo/Redo auch im Mobile-Dropdown (auf Desktop versteckt weil Button da ist) */}
                {canUndo && (
                  <>
                    <DropdownMenuItem onClick={performUndo} disabled={!lastUndo || loadingUndo} className="md:hidden">
                      <Undo2 className="mr-2 h-4 w-4" /> Rückgängig
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={performRedo} disabled={!hasRedo || loadingRedo} className="md:hidden">
                      <Redo2 className="mr-2 h-4 w-4" /> Wiederholen
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="md:hidden" />
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" /> Einstellungen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* Logout in Rot damit man's nicht aus Versehen klickt */}
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* ---- Hauptinhalt — hier wird die aktuelle Seite reingeladen ---- */}
      {/* extra Bottom-Padding auf Mobile wegen der Tab-Bar unten */}
      <main className="flex-1 pb-[calc(72px+env(safe-area-inset-bottom))] md:pb-0">
        {/* Outlet = Platzhalter wo React Router die aktuelle Unterseite reinrendert */}
        <Outlet />
      </main>

      {/* iOS-spezifischer Install-Hinweis (Safari hat kein beforeinstallprompt) */}
      <IOSInstallHint />

      {/* ---- Mobile Bottom Tab Bar (nur auf kleinen Screens, wie eine native App) ---- */}
      {/* safe-area-bottom berücksichtigt iPhone Home-Indicator Abstand */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t safe-area-bottom">
        <div className="flex h-[64px] items-stretch" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn(
                "flex flex-col items-center justify-center gap-1 flex-1 text-[10px] font-semibold transition-all duration-150 select-none",
                // aktiver Tab in Primärfarbe, inaktive in gedämpft grau
                isActive ? "text-primary" : "text-muted-foreground active:text-foreground"
              )}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  {/* aktiver Tab hat einen kleinen Pill-Hintergrund — iOS-Style */}
                  <div className={cn("rounded-xl px-5 py-1.5 transition-all duration-150", isActive ? "bg-primary/15" : "")}>
                    <item.icon className={cn("h-5 w-5", isActive ? "stroke-[2.5]" : "stroke-[1.5]")} />
                  </div>
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Overlay der interaktiven Hilfe-Tour — rendert nur, solange eine Tour aktiv ist */}
      <TourOverlay />
    </div>
  );
};

export default Layout;
